import DifferenceIcon from "@mui/icons-material/Difference";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { getRecordRoles, getRecordRolesNote } from "components/RoleSelect/recordRoles";
import { releaseVersion } from "utils/service";
import badgeStyle from "./Badges.module.scss";

// State badges render as small, inert ICONS that reveal their label on hover
// (Margot, operator-task-fe-list-view-row-height-harmonize-real, screenshot-1464):
// the written text chips wrapped onto a clipped second row under the record name;
// an icon sits inline on the name line, in the same place / style as the
// comment-count indicator, and shows its label on hover. Colour follows the badge
// severity via palette tokens only (ui-design-principle-color-palette); it is a
// quiet indicator, not a button (ui-design-principle-affordance-interactivity).
const getBadge = (label: string, icon: any, type: "info" | "warning" | "error" | "success") => (
    <Tooltip overlay={label} key={label} overlayStyle={{ zIndex: 20000 }}>
        <span
            className={`${badgeStyle.stateIconBadge} ${badgeStyle[`stateIconBadge--${type}`]}`}
            aria-label={label}
            data-badge-type={type}
        >
            <Icon icon={icon} />
        </span>
    </Tooltip>
);

const getInfoChip = (label: string, icon: any) => getBadge(label, icon, "info");
const getWarnChip = (label: string, icon: any) => getBadge(label, icon, "warning");
const getErrorChip = (label: string, icon: any) => getBadge(label, icon, "error");

export const ReadOnlyBadges = ({ data }: any) => {
    return (
        <>
            {data?.is_readonly && getInfoChip("Read-only", IconCollection.lockOutline)}
            {data?.is_bundle_readonly &&
                getInfoChip("Read-only bundle", IconCollection.lockOutline)}
            {data?.is_project_readonly &&
                getInfoChip("Read-only project", IconCollection.lockOutline)}
        </>
    );
};

// "In trash" is a destructive/removed state — colour it red (error severity) so
// it reads as stronger than the quiet neutral info badges and is clearly distinct
// from the yellow "Changed" attention badge (Margot,
// operator-task-fe-list-view-row-height-harmonize-real, 2026-09-30: "make the
// in-trash badge yellow or red").
export const InTrashBadge = ({ deletedAt }: { deletedAt?: string | null }) =>
    deletedAt ? getErrorChip("In trash", IconCollection.deleteOutline) : null;

export const DeprecationBadge = ({ hasDeprecation }: { hasDeprecation: boolean | null }) =>
    hasDeprecation ? getWarnChip("Has deprecation", IconCollection.alertCircleOutline) : null;

export const SyntaxErrorBadge = ({ hasSyntaxError }: { hasSyntaxError: boolean | null }) =>
    hasSyntaxError ? getErrorChip("Syntax error", IconCollection.alert) : null;

// A flow, wrapper or scheduler that carries roles runs with them; the hover names
// the roles and which of them the starter must hold (same wording as the Run
// button). The row data comes from the listing body's identity_role relation.
export const RolesBadge = ({ data }: { data?: any }) => {
    const note = getRecordRolesNote(getRecordRoles(data), "this record");
    return note ? getInfoChip(note, IconCollection.shieldAccountOutline) : null;
};

export const BundleContentBadge = ({ isBundleContent }: { isBundleContent?: boolean }) =>
    isBundleContent ? getInfoChip("Bundle content", IconCollection.packageVariant) : null;

// "Changed" = the bundle has uncommitted content changes (git-dirty). Use the
// Material "difference" glyph (Margot picked it explicitly, codepoint eb7d), not
// the edit pencil (pencil reads as "this is editable") nor the source-commit
// glyph. Colour it yellow (warning severity), NOT green: green signals
// "everything is good", whereas this badge indicates a potential need for the
// user to act (commit the pending changes). Yellow = "attention / action
// pending" and is distinct from the neutral-grey info badges and the red
// destructive ones (Margot, operator-task-fe-list-view-row-height-harmonize-real,
// 2026-09-30).
export const ChangedBundleBadge = ({ isDirty }: { isDirty?: boolean }) =>
    isDirty ? getWarnChip("Changed", DifferenceIcon) : null;

export const PendingTrashBadge = ({
    pendingTrashIdentityId,
}: {
    pendingTrashIdentityId?: string | null;
}) =>
    pendingTrashIdentityId
        ? getInfoChip("Pending move to trash", IconCollection.deleteAlertOutline)
        : null;

export const PendingRestoreBadge = ({
    pendingRestoreIdentityId,
}: {
    pendingRestoreIdentityId?: string | null;
}) => (pendingRestoreIdentityId ? getInfoChip("Pending restore", IconCollection.restore) : null);

export const PendingDeleteBadge = ({
    pendingDeleteIdentityId,
}: {
    pendingDeleteIdentityId?: string | null;
}) =>
    pendingDeleteIdentityId
        ? getInfoChip("Pending delete", IconCollection.deleteAlertOutline)
        : null;

export const BootstrapUserBadge = ({ isBootstrapUser }: { isBootstrapUser?: boolean }) =>
    isBootstrapUser ? getInfoChip("Bootstrap user", IconCollection.shieldAccountOutline) : null;

// A bundle whose last install/update resolved against an earlier platform major
// than the one the workspace now runs (backend `bundle.target_major` vs the
// build-time `releaseVersion`). It reads as "stuck on an old major — a
// re-install/update against the current version may be available". Yellow
// (warning severity, like the Changed badge): attention / action pending, not an
// error. Rendered only when both majors are known numeric versions that differ,
// so a "dev" build or a bundle never resolved through an install shows nothing.
export const OutdatedMajorBundleBadge = ({ targetMajor }: { targetMajor?: string | null }) => {
    const currentMajor = releaseVersion;
    if (!targetMajor || !/^\d+$/.test(currentMajor) || targetMajor === currentMajor) {
        return null;
    }
    return getWarnChip(
        `Installed for platform version ${targetMajor}; this workspace runs version ${currentMajor}. A re-install or update against the current version may be available.`,
        IconCollection.update
    );
};
